import axios, { HeadersDefaults } from "axios";
import { forceToLogOut, setCurrentPathname } from "@/helper/utility";
import { getRefreshToken, getToken, setAuthToken, setRefreshToken } from "@/helper/storage";
import { excludedToLogout, excludedUrl401 } from "../constants/excludeUrls";
import { REFRESH_TOKEN } from "@/constants/urls";
import { BASE_URL_ENV } from "@/constants/defaultValues";
import { devLogError } from "./sentryCaptureException";
import pMemoize from "p-memoize";
import ExpiryMap from "expiry-map";

const axiosInstance = axios.create();

const obtainNewAccessToken = async () => {
  try {
    const rs = await axiosInstance.post(REFRESH_TOKEN, {
      refresh_token: getRefreshToken()!,
    });
    const access: string = rs.data.access_token;
    const refresh: string = rs.data.refresh_token;

    setAuthToken(access);
    setRefreshToken(refresh);

    return access;
  } catch (error) {
    devLogError(error);
    return Promise.reject(error);
  }
};

const cache = new ExpiryMap(10000); // Cached values expire after 10 seconds

const memoizedRefreshToken = pMemoize(obtainNewAccessToken, { cache });

axiosInstance.defaults.baseURL = BASE_URL_ENV;

type headers = {
  [key: string]: string;
  "Content-Type": string;
  Accept: string;
  Authorization: string;
};

axiosInstance.defaults.headers = {
  "Content-Type": "application/json",
  Accept: "application/json",
} as headers & HeadersDefaults;

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      // Configure this as per your backend requirements
      config.headers!["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return error;
    // return Promise.reject(error);
  }
);
//TODO: refactor below lines for general usage
axiosInstance.interceptors.response.use(
  (res) => {
    if (!res.config.url?.endsWith("/ai-images")) {
      return res.data;
    } else {
      return res;
    }
  },
  async (err) => {
    const originalConfig = err.config;

    if (err?.response?.status >= 500 && !originalConfig._retry) {
      try {
        originalConfig._retry = true;
        return axiosInstance(originalConfig);
      } catch (error) {
        console.log("# AXIOS error", error);

        return Promise.reject(error);
      }
    }

    if (!excludedUrl401.includes(originalConfig.url) && err?.response?.status === 401) {
      // Access Token was expired
      if (getRefreshToken()) {
        if (!originalConfig._retry) {
          originalConfig._retry = true;

          try {
            const updatedToken = await memoizedRefreshToken();

            originalConfig.headers["Authorization"] = `Bearer ${updatedToken}`;
            return axiosInstance(originalConfig);
          } catch (error) {
            if (typeof window !== "undefined") {
              setCurrentPathname(`${window.location.pathname + window.location.search}`);
            } else {
              setCurrentPathname("/");
            }
            if (!excludedToLogout.includes(originalConfig.url)) {
              forceToLogOut("Expire Refresh Token", originalConfig.url, err?.response?.status);
            }
            return Promise.reject(error);
          }
        }
      } else {
        if (!excludedToLogout.includes(originalConfig.url)) {
          forceToLogOut("No Refresh Token or Retry", originalConfig.url, err?.response?.status);
        }
        return Promise.reject(err.response);
      }
    }

    return Promise.reject(err.response);
  }
);

export default axiosInstance;
// axios interceptor refresh token infinite loop
